import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import {
  api,
  type Category,
  type CategoryDisplayMode,
  type Item,
  type ItemPage,
  type SpaceDetail,
} from '../../lib/api'
import { invaliderFiches } from '../../lib/cache'
import { CategoryMark } from '../spaces/CategoryMark'
import { ItemCard } from './ItemCard'
import { Cadenas } from './Verrou'
import { AddSheet } from './AddSheet'
import { TabBar } from '../../components/TabBar'
import { layout, sousLaBarre } from '../../design/tokens'
import { BoutonActualiser } from '../../components/BoutonActualiser'

/**
 * Planche 05 du cahier des charges — « Récents ».
 *
 * « Mosaïque 2 colonnes, groupée par date relative — la mémoire humaine fonctionne par c'était
 * la semaine dernière. Les puces filtrent sans quitter l'écran. »
 */
export function RecentsScreen() {
  const { spaceId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const [feuille, setFeuille] = useState(false)

  const categorie = params.get('categorie')
  // Dans l'adresse et non dans un état local : la vue se partage et survit au retour arrière.
  const favoris = params.get('favoris') === '1'
  const archives = params.get('archives') === '1'
  const recherche = params.get('q') ?? ''
  const [saisie, setSaisie] = useState(recherche)
  const [chercheOuvert, setChercheOuvert] = useState(recherche.length > 0)
  const [filtreOuvert, setFiltreOuvert] = useState(false)


  /** Conserve les filtres en place et n'en change qu'un. */
  const majParams = (changements: Record<string, string | null>) => {
    const suivant: Record<string, string> = {}
    if (categorie) suivant['categorie'] = categorie
    if (favoris) suivant['favoris'] = '1'
    if (archives) suivant['archives'] = '1'
    if (recherche) suivant['q'] = recherche

    for (const [cle, valeur] of Object.entries(changements)) {
      if (valeur === null) {
        delete suivant[cle]
      } else {
        suivant[cle] = valeur
      }
    }

    setParams(suivant)
  }

  const { data: espace } = useQuery<SpaceDetail>({
    queryKey: ['espace', spaceId],
    queryFn: () => api.space(spaceId),
  })

  // Le mode enregistré de la catégorie dit l'habitude ; « affichage » dans l'adresse permet de
  // basculer pour un coup d'œil sans rien changer durablement. Hors catégorie, la mosaïque
  // reste la présentation par défaut de la planche 10.
  const categorieCourante = espace?.categories.find((c) => c.id === categorie)
  const modeEnregistre = categorieCourante?.displayMode ?? 'cards'
  const mode = (params.get('affichage') as CategoryDisplayMode | null) ?? modeEnregistre

  const { data, isPending } = useQuery<ItemPage>({
    queryKey: ['fiches', spaceId, categorie, favoris, archives, recherche],
    queryFn: () =>
      api.items(spaceId, {
        ...(categorie ? { categoryId: categorie } : {}),
        ...(favoris ? { favorite: true } : {}),
        ...(recherche ? { q: recherche } : {}),
        // Sans statut, les archives ne remontent pas. C'est ce qui rendait introuvable une
        // fiche mise en favori puis supprimée (correction S47).
        ...(archives ? { status: 'archived' as const } : {}),
      }),
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: sousLaBarre(32),
      }}
    >
      <header className="flex items-center justify-between gap-3" style={{ height: layout.header }}>
        <h1 className="grow truncate font-serif text-screen">
          {archives ? 'Archives' : (categorieCourante?.name ?? 'Récents')}
        </h1>
        <button
          type="button"
          aria-label="Rechercher"
          aria-pressed={chercheOuvert}
          onClick={() => {
            if (chercheOuvert) {
              setSaisie('')
              majParams({ q: null })
            }
            setChercheOuvert((ouvert) => !ouvert)
          }}
          className="flex shrink-0 items-center justify-center rounded-full border border-border bg-card text-accent"
          style={{ width: layout.minimumTouchTarget, height: layout.minimumTouchTarget }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
        </button>
        <button
          type="button"
          aria-label={
            mode === 'cards' ? 'Afficher en liste' : 'Afficher en cartes'
          }
          title={mode === 'cards' ? 'Afficher en liste' : 'Afficher en cartes'}
          onClick={() =>
            majParams({
              affichage:
                mode === 'cards'
                  ? modeEnregistre === 'checklist'
                    ? 'checklist'
                    : 'list'
                  : 'cards',
            })
          }
          className="flex shrink-0 items-center justify-center rounded-full border border-border bg-card text-accent"
          style={{ width: layout.minimumTouchTarget, height: layout.minimumTouchTarget }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            {mode === 'cards' ? (
              <>
                <path d="M4 6h16" />
                <path d="M4 12h16" />
                <path d="M4 18h16" />
              </>
            ) : (
              <>
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </>
            )}
          </svg>
        </button>
        <BoutonActualiser />
      </header>

      {chercheOuvert ? (
        <form
          onSubmit={(evenement) => {
            evenement.preventDefault()
            majParams({ q: saisie.trim() || null })
          }}
          className="flex gap-2"
          style={{ paddingBottom: 12 }}
        >
          <input
            autoFocus
            value={saisie}
            onChange={(evenement) => setSaisie(evenement.target.value)}
            placeholder={categorie ? 'Chercher dans cette catégorie' : 'Chercher partout'}
            className="grow rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16 }}
          />
          <button
            type="submit"
            className="rounded-button bg-accent px-4 text-meta font-semibold text-card"
            style={{ height: 48 }}
          >
            Chercher
          </button>
        </form>
      ) : null}

      {/* La recherche suit le filtre : dans une catégorie elle s'y limite, sur « Tout » elle
          porte sur tout l'espace. C'est le comportement attendu et il ne demande aucun réglage. */}
      {recherche ? (
        <p className="text-meta text-ink-muted" style={{ paddingBottom: 12 }}>
          Résultats pour « {recherche} »
          {categorie ? ' dans cette catégorie' : ''}.{' '}
          <button
            type="button"
            onClick={() => {
              setSaisie('')
              majParams({ q: null })
            }}
            className="font-semibold text-accent"
          >
            Effacer
          </button>
        </p>
      ) : null}

      {/* Une barre de filtre, et non la liste de toutes les catégories : avec neuf catégories,
          le mur de puces occupait quatre lignes avant le premier contenu, uniquement pour dire
          où l'on se trouve déjà. Elle s'ouvre quand on veut changer (correction S62). */}
      <div className="flex flex-wrap items-center gap-2" style={{ paddingBottom: 12 }}>
        <button
          type="button"
          onClick={() => setFiltreOuvert((ouvert) => !ouvert)}
          aria-expanded={filtreOuvert}
          className="flex items-center gap-2 rounded-full border border-border bg-card px-4 text-meta font-semibold"
          style={{ minHeight: 40 }}
        >
          {categorieCourante ? (
            <CategoryMark
              initials={categorieCourante.initials}
              hue={categorieCourante.hue}
              size="small"
            />
          ) : null}
          <span>{categorieCourante?.name ?? (favoris ? 'Favoris' : 'Tout')}</span>
          <span aria-hidden="true" className="text-ink-muted">
            ▾
          </span>
        </button>

        {categorie || favoris ? (
          <button
            type="button"
            onClick={() => majParams({ categorie: null, favoris: null })}
            className="text-meta font-semibold text-accent"
          >
            Tout voir
          </button>
        ) : null}
      </div>

      {filtreOuvert ? (
        <Puces
          categories={espace?.categories ?? []}
          active={categorie}
          favoris={favoris}
          onChange={(valeur) => {
            majParams({ categorie: valeur })
            setFiltreOuvert(false)
          }}
          onFavoris={() => {
            majParams({ favoris: favoris ? null : '1' })
            setFiltreOuvert(false)
          }}
        />
      ) : null}

      {/* Deux onglets et non une puce parmi les filtres : les archives sont un autre endroit,
          pas un filtre de plus. Placées au milieu des catégories, elles étaient introuvables. */}
      <nav
        className="mt-3 flex rounded-full border border-border bg-card p-1"
        style={{ gap: 4 }}
      >
        <Onglet libelle="En cours" actif={!archives} onClick={() => majParams({ archives: null })} />
        <Onglet
          libelle="Archivées"
          actif={archives}
          onClick={() => majParams({ archives: '1' })}
        />
      </nav>

      {isPending ? (
        <Squelette />
      ) : data && data.items.length > 0 ? (
        <div className="mt-4 flex flex-col gap-6">
          {grouper(data.items).map((groupe) => (
            <section key={groupe.libelle}>
              <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">
                {groupe.libelle}
              </h2>
              {mode === 'cards' ? (
                <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
                  {groupe.fiches.map((item) => (
                    <li key={item.id}>
                      <ItemCard item={item} to={`/espace/${spaceId}/fiche/${item.id}`} />
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="flex flex-col gap-2">
                  {groupe.fiches.map((item) => (
                    <Rangee
                      key={item.id}
                      item={item}
                      spaceId={spaceId}
                      cochable={mode === 'checklist'}
                    />
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">
            {recherche
              ? 'Rien ne correspond.'
              : archives
                ? 'Aucune fiche archivée ici.'
                : favoris
                  ? 'Aucun favori ici.'
                  : "Aucune idée ici pour l'instant."}
          </p>
          <p className="mt-2 text-body text-ink-muted">
            {recherche
              ? 'La recherche porte sur le titre, le texte et le domaine.'
              : archives
                ? 'Une fiche supprimée arrive ici, et peut en repartir.'
                : favoris
                  ? "Ouvrez une fiche et touchez le cœur pour l'y ajouter."
                  : 'Collez un lien, ou écrivez une note.'}
          </p>
        </div>
      )}

      <TabBar onAdd={() => setFeuille(true)} activeSpaceId={spaceId} />

      {/* Ajouter depuis une catégorie filtrée y range directement. */}
      {feuille ? (
        <AddSheet
          spaceId={spaceId}
          onClose={() => setFeuille(false)}
          defaultCategoryId={categorie}
        />
      ) : null}
    </main>
  )
}

/**
 * « Les puces filtrent sans quitter l'écran. » Le filtre vit dans l'URL, donc il survit au retour.
 *
 * Les puces passent à la ligne au lieu de défiler horizontalement. La planche 05 les montre sur
 * une seule ligne tronquée — mais en pratique la puce active se retrouvait à moitié hors écran,
 * avec une barre de défilement par-dessus. Sept puces courtes tiennent sur deux lignes : tout est
 * visible et atteignable, sans rien à faire glisser.
 */
/**
 * Une fiche en rangée compacte. Avec sa case quand la catégorie se coche.
 *
 * Cocher n'archive pas : c'est un état distinct, exactement comme dans la liste de courses où
 * cocher fait descendre l'article sans le supprimer. Une recette cochée reste là, rayée, et se
 * décoche le jour où on veut la refaire.
 */
function Rangee({
  item,
  spaceId,
  cochable,
}: {
  item: Item
  spaceId: string
  cochable: boolean
}) {
  const queryClient = useQueryClient()

  const cocher = useMutation({
    mutationFn: (valeur: boolean) => api.patchItem(spaceId, item.id, { isDone: valeur }),
    onSuccess: () => invaliderFiches(queryClient, spaceId),
  })

  return (
    <li className="flex items-center gap-3 rounded-card border border-border bg-card p-3">
      {cochable ? (
        <input
          type="checkbox"
          checked={item.isDone}
          onChange={(evenement) => cocher.mutate(evenement.target.checked)}
          aria-label={item.isDone ? `Décocher ${item.title}` : `Cocher ${item.title}`}
          style={{ width: 22, height: 22, flexShrink: 0 }}
        />
      ) : item.categoryInitials && item.categoryHue !== null ? (
        <CategoryMark initials={item.categoryInitials} hue={item.categoryHue} size="small" />
      ) : null}

      <Link to={`/espace/${spaceId}/fiche/${item.id}`} className="grow truncate text-ink">
        <span
          className="block truncate text-card-title font-semibold"
          style={
            item.isDone && cochable
              ? { textDecoration: 'line-through', color: 'var(--color-ink-muted)' }
              : undefined
          }
        >
          {item.title}
        </span>
        {item.domain ? (
          <span className="block truncate text-meta text-ink-muted">{item.domain}</span>
        ) : null}
      </Link>

      {item.isLocked ? (
        <span aria-label="Fiche protégée" className="text-ink-muted">
          <Cadenas taille={15} />
        </span>
      ) : null}
      {item.isFavorite ? <span aria-label="Favori" className="text-accent">♥</span> : null}
    </li>
  )
}

function Puces({
  categories,
  active,
  favoris,
  onChange,
  onFavoris,
}: {
  categories: readonly Category[]
  active: string | null
  favoris: boolean
  onChange: (valeur: string | null) => void
  onFavoris: () => void
}) {
  return (
    <ul className="flex flex-wrap gap-2" style={{ paddingBottom: 12 }}>
      <li>
        <Puce libelle="Tout" actif={active === null} onClick={() => onChange(null)} />
      </li>
      {/* Le filtre des favoris se cumule avec une catégorie : « les recettes qu'on aime » est
          une question plus utile que « tous les favoris » ou « toutes les recettes ». */}
      <li>
        <Puce libelle="♥ Favoris" actif={favoris} onClick={onFavoris} />
      </li>
      {categories.map((category) => (
        <li key={category.id}>
          <Puce
            libelle={category.name}
            actif={active === category.id}
            onClick={() => onChange(category.id)}
          />
        </li>
      ))}
    </ul>
  )
}

/** Un onglet du couple « En cours / Archivées ». */
function Onglet({
  libelle,
  actif,
  onClick,
}: {
  libelle: string
  actif: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      className="grow rounded-full text-meta font-semibold"
      style={{
        minHeight: 40,
        background: actif ? 'var(--color-accent)' : 'transparent',
        color: actif ? 'var(--color-card)' : 'var(--color-ink-muted)',
      }}
    >
      {libelle}
    </button>
  )
}

function Puce({ libelle, actif, onClick }: { libelle: string; actif: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      className="whitespace-nowrap rounded-full px-4 text-meta font-semibold"
      style={{
        minHeight: 36,
        background: actif ? 'var(--color-accent)' : 'var(--color-card)',
        color: actif ? 'var(--color-card)' : 'var(--color-ink)',
        border: `1px solid ${actif ? 'var(--color-accent)' : 'var(--color-border)'}`,
      }}
    >
      {libelle}
    </button>
  )
}

type Groupe = { libelle: string; fiches: ItemPage['items'][number][] }

/**
 * Groupement par date relative. Les bornes sont volontairement grossières : on cherche à
 * retrouver, pas à dater.
 */
function grouper(items: ItemPage['items']): Groupe[] {
  const maintenant = new Date()
  const debutDuJour = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate())
  const debutDeSemaine = new Date(debutDuJour.getTime() - 7 * 24 * 60 * 60 * 1000)

  const groupes: Groupe[] = [
    { libelle: "Aujourd'hui", fiches: [] },
    { libelle: 'Cette semaine', fiches: [] },
    { libelle: 'Plus tôt', fiches: [] },
  ]

  for (const item of items) {
    const date = new Date(item.createdAt)
    const cible = date >= debutDuJour ? 0 : date >= debutDeSemaine ? 1 : 2
    groupes[cible]!.fiches.push(item)
  }

  return groupes.filter((groupe) => groupe.fiches.length > 0)
}

function Squelette() {
  return (
    <ul className="mt-4 grid grid-cols-2" style={{ gap: layout.gridGap }}>
      {[0, 1, 2, 3].map((index) => (
        <li key={index} className="overflow-hidden rounded-card border border-border bg-card">
          <div className="w-full bg-skeleton-image" style={{ aspectRatio: '4 / 3' }} />
          <div className="p-3">
            <div className="rounded-field bg-sunken" style={{ height: 17 }} />
            <div className="mt-2 rounded-field bg-sunken" style={{ height: 13, width: '60%' }} />
          </div>
        </li>
      ))}
    </ul>
  )
}
